<script setup lang="ts">
import { computed } from 'vue';
import { gaiaFontSize, gaiaFontWeight, gaiaNeutral } from '@/components/design/themes/tokens';
import { NButton, NIcon } from 'naive-ui';
import { IconAdd } from '@/components/design/icons';
import type { TemplateField, TemplateFieldType } from '@/api';
import { buildDefaultItem } from '@/composables/contractGeneration/useExtractedSchema';
import { useTemplateFieldMetadataMap } from '@/composables/contractGeneration/useTemplateFieldMetadata';
import { orderSiblings } from '@/utils/contractGeneration/templateFieldLabels';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import SchemaField from './SchemaField.vue';
import EntityField from './EntityField.vue';

interface Props {
  field: TemplateField;
  modelValue: unknown;
  rootFormData: Record<string, unknown>;
  /**
   * Scope chain of this list's own enclosing scopes (innermost → outermost).
   * We prepend the current item when we pass `localScopes` down to each
   * child `SchemaField` so that a condition written inside a nested list
   * item can still reach fields on the enclosing loop's item by walking up.
   */
  parentScopes?: unknown[];
  path: string;
  invalidPaths: Set<string>;
  disabled?: boolean;
  /**
   * When the parent list's item_type is `legal_entity` / `natural_person`,
   * render each item through EntityField (search/create/manual) instead of
   * the plain object form. Null for non-entity lists.
   */
  entityItemType?: TemplateFieldType | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: Record<string, unknown>[]];
}>();

const itemFields = computed(() => props.field.item_fields ?? {});

// Item fields in the author's order. Their group hangs off the list's `[]`
// marker (`services[].fee`), not off the list's own path — passing that
// would look up `services.fee` and silently fall back to document order.
const metadataMap = useTemplateFieldMetadataMap();
const orderedItemFields = computed(() =>
  orderSiblings(Object.entries(itemFields.value), `${props.path}[]`, metadataMap.value)
);

// When the list's item_type is an entity, build a synthetic TemplateField for
// each item so it can route through EntityField (which expects `field.fields`,
// `field.type`, etc.). The wrapper inherits `required` from the list itself
// only loosely — list-level required means "at least one item", not that
// every item must be filled.
const entityItemField = computed<TemplateField | null>(() => {
  if (!props.entityItemType) return null;
  return {
    type: props.entityItemType,
    fields: props.field.item_fields ?? {},
    required: false,
  };
});

const items = computed<Record<string, unknown>[]>(() => {
  if (!Array.isArray(props.modelValue)) return [];
  return props.modelValue.map(item =>
    item && typeof item === 'object' && !Array.isArray(item)
      ? (item as Record<string, unknown>)
      : {}
  );
});

function handleItemFieldUpdate(itemIndex: number, childKey: string, childValue: unknown): void {
  const next = items.value.map((item, index) => {
    if (index !== itemIndex) return item;
    return { ...item, [childKey]: childValue };
  });
  emit('update:modelValue', next);
}

function handleEntityItemUpdate(itemIndex: number, value: Record<string, unknown> | null): void {
  const next = items.value.map((item, index) => (index === itemIndex ? (value ?? {}) : item));
  emit('update:modelValue', next);
}

function addItem(): void {
  emit('update:modelValue', [...items.value, buildDefaultItem(itemFields.value)]);
}

function removeItem(index: number): void {
  const next = items.value.filter((_, i) => i !== index);
  emit('update:modelValue', next);
}

const canRemove = computed(() => !props.field.required || items.value.length > 1);

// --- chrome (tokens for the scoped styles) ---
const fsRow = gaiaFontSize.row;
const fwSemibold = String(gaiaFontWeight.semibold);
const gBorder = gaiaNeutral.borderCard;
const gSoft = gaiaNeutral.surfaceSoft;
const gSubtle = gaiaNeutral.textSubtle;
const gSurface = gaiaNeutral.surface;
const gText = gaiaNeutral.text;
</script>

<template>
  <div class="list-field">
    <div v-if="items.length === 0" class="list-field__empty">
      <span class="empty-text">No items yet.</span>
    </div>

    <div v-else class="list-field__items">
      <div
        v-for="(_, itemIndex) in items"
        :key="itemIndex"
        class="list-item"
        :style="{ '--schema-field-delay': `${itemIndex * 160}ms` }"
      >
        <div class="list-item__header">
          <span class="list-item__index">#{{ itemIndex + 1 }}</span>
          <gaia-delete-button
            v-if="canRemove"
            intent="remove"
            variant="label"
            size="xs"
            :disabled="disabled"
            @click="removeItem(itemIndex)"
          />
        </div>
        <div class="list-item__fields">
          <EntityField
            v-if="entityItemField"
            :field-key="`Entry ${itemIndex + 1}`"
            :field="entityItemField"
            :model-value="items[itemIndex]"
            :root-form-data="rootFormData"
            :local-scopes="parentScopes ?? []"
            :path="`${path}[${itemIndex}]`"
            :invalid-paths="invalidPaths"
            :disabled="disabled"
            hide-container-header
            @update:model-value="value => handleEntityItemUpdate(itemIndex, value)"
          />
          <template v-else>
            <SchemaField
              v-for="([childKey, childField], fieldIndex) in orderedItemFields"
              :key="childKey"
              :field-key="childKey"
              :field="childField"
              :model-value="items[itemIndex][childKey]"
              :root-form-data="rootFormData"
              :local-scopes="[items[itemIndex], ...(parentScopes ?? [])]"
              :path="`${path}[${itemIndex}].${childKey}`"
              :invalid-paths="invalidPaths"
              :disabled="disabled"
              :style="{ '--schema-field-delay': `${fieldIndex * 120}ms` }"
              @update:model-value="value => handleItemFieldUpdate(itemIndex, childKey, value)"
            />
          </template>
        </div>
      </div>
    </div>

    <div class="list-field__actions">
      <NButton size="small" dashed :disabled="disabled" @click="addItem">
        <template #icon>
          <NIcon><IconAdd /></NIcon>
        </template>
        Add item
      </NButton>
    </div>
  </div>
</template>

<style scoped>
.list-field {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.list-field__empty {
  padding: 12px;
  color: v-bind(gSubtle);
  font-size: v-bind(fsRow);
  font-style: italic;
  background: v-bind(gSoft);
  border-radius: 6px;
  border: 1px dashed v-bind(gBorder);
}

.list-field__items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.list-item {
  border: 1px solid v-bind(gBorder);
  border-radius: 8px;
  padding: 12px 16px;
  background: v-bind(gSurface);
  animation: list-item-in 520ms cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: var(--schema-field-delay, 0ms);
}

@keyframes list-item-in {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .list-item {
    animation: none;
  }
}

.list-item__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.list-item__index {
  font-weight: v-bind(fwSemibold);
  color: v-bind(gText);
  font-size: v-bind(fsRow);
}

.list-item__fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.list-field__actions {
  display: flex;
  justify-content: flex-start;
}
</style>
